import { useState } from 'react';
import { C, PLAIN, SKIP_REASON, fmtS, fmt } from '../constants';
import { normName, plainifyNote } from '../utils/helpers';

export default function ModelCard({ model, currentPrice }) {
  const [expanded, setExpanded] = useState(false);
  const displayName = normName(model.name);
  const formula = model.formula || model.explanation || "";
  const note = model.note || model.exclusionReason || "";

  if (!model.included) {
    const skip = SKIP_REASON[displayName] || SKIP_REASON[model.name] || null;
    const skipText = skip?.reason || plainifyNote(note) || "This model doesn't apply to this type of stock.";
    return (
      <div style={{ border:"1px dashed rgba(255,255,255,0.08)", borderRadius:16, padding:"14px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
        <span style={{ fontFamily:"'Syne',sans-serif", fontSize:11, fontWeight:700, color:C.muted, border:"1px solid rgba(255,255,255,0.1)", padding:"3px 7px", borderRadius:4, letterSpacing:".08em", textTransform:"uppercase", flexShrink:0, whiteSpace:"nowrap", marginTop:1 }}>Skipped</span>
        <div style={{ fontSize:12, color:C.muted, lineHeight:1.55 }}>
          <strong style={{ color:"rgba(255,255,255,0.42)" }}>{displayName}</strong> — {skipText}
        </div>
      </div>
    );
  }

  const dollar = model.fairValue != null ? model.fairValue - currentPrice : null;
  const pct    = dollar != null ? (dollar/currentPrice)*100 : null;
  const up     = dollar != null && dollar >= 0;
  const weight = model.weight != null ? Math.round(model.weight * 100) : null;
  const barW   = Math.min(Math.abs(pct||0)*1.5, 100);
  const conf   = (model.reliability||model.confidence||model.confidenceLevel||"medium").toLowerCase();
  const confNorm = conf.includes("high") ? "high" : conf.includes("low") ? "low" : "medium";
  const confCfg = {
    high:   { label:"High Confidence",   color:C.green,  bg:"rgba(0,200,5,0.09)",    border:"rgba(0,200,5,0.24)"    },
    medium: { label:"Medium Confidence", color:C.gold2,  bg:"rgba(201,168,76,0.09)", border:"rgba(201,168,76,0.26)" },
    low:    { label:"Low Confidence",    color:"#FCA5A5", bg:"rgba(255,59,48,0.07)",  border:"rgba(255,59,48,0.2)"   },
  }[confNorm];

  // Shared full card body content
  const cardBody = (
    <>
      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:12.5, color:C.soft, lineHeight:1.65, marginBottom:10 }}>
        {PLAIN[displayName]||PLAIN[model.name]||""}
      </div>
      {dollar != null && (
        <div style={{ background:up?"rgba(0,200,5,0.07)":"rgba(255,59,48,0.07)", border:`1px solid ${up?"rgba(0,200,5,0.22)":"rgba(255,59,48,0.22)"}`, borderRadius:10, padding:"10px 12px", marginBottom:8 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:5 }}>
            <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".08em", textTransform:"uppercase" }}>Fair Value</span>
            <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:22, fontWeight:800, color:C.text }}>{fmtS(model.fairValue)}</span>
          </div>
          <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:15, fontWeight:700, color:up?C.green:C.neg, marginBottom:5 }}>
            {up?"+":"-"}{fmt(Math.abs(dollar))} · {up?"+":""}{pct?.toFixed(1)}%
          </div>
          <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${barW}%`, background:up?`linear-gradient(90deg,rgba(0,200,5,0.2),${C.green})`:`linear-gradient(90deg,rgba(255,59,48,0.2),${C.neg})`, borderRadius:2 }}/>
          </div>
        </div>
      )}
      {formula && (
        <div className="hma-model-formula" style={{ fontFamily:"'Inter',sans-serif", fontSize:11.5, background:C.bg3, borderLeft:`2px solid ${C.bord}`, borderRadius:8, padding:"8px 11px", color:C.soft, lineHeight:1.55, marginBottom:7 }}>
          {formula}
        </div>
      )}
      <div style={{ display:"inline-flex", alignItems:"center", gap:4, fontFamily:"'Syne',sans-serif", fontSize:11, fontWeight:700, letterSpacing:".06em", padding:"3px 8px", borderRadius:4, background:confCfg.bg, border:`1px solid ${confCfg.border}`, color:confCfg.color, textTransform:"uppercase" }}>
        ● {confCfg.label}
      </div>
    </>
  );

  return (
    <div className="hma-model-card" style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:16, overflow:"hidden" }}>
      <style>{`
        .hma-mc-desktop { display:block }
        .hma-mc-trigger { display:none }
        @media(max-width:768px){
          .hma-mc-desktop { display:none !important }
          .hma-mc-trigger { display:flex !important }
        }
        .hma-mc-chevron { display:inline-block; transition:transform 0.2s ease }
      `}</style>

      {/* Desktop: full card always visible */}
      <div className="hma-mc-desktop" style={{ padding:"16px 18px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <span style={{ fontFamily:"'Space Mono',monospace", fontSize:10.5, fontWeight:700, color:C.gold2, letterSpacing:".06em" }}>{displayName}</span>
          {weight!=null && weight>0 && <span style={{ fontFamily:"'Inter',sans-serif", fontSize:11, color:C.muted }}>Weight: {weight}%</span>}
        </div>
        {cardBody}
      </div>

      {/* Mobile: collapsed accordion row */}
      <button
        className="hma-mc-trigger"
        onClick={()=>setExpanded(v=>!v)}
        style={{ width:"100%", minHeight:44, padding:"12px 16px", background:"transparent", border:"none", cursor:"pointer", color:"inherit", alignItems:"center", justifyContent:"space-between", gap:8, textAlign:"left" }}
      >
        <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, fontWeight:700, color:C.gold2, letterSpacing:".05em", flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {displayName}
        </span>
        {model.fairValue != null && (
          <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:16, fontWeight:800, color:C.text, whiteSpace:"nowrap" }}>
            {fmtS(model.fairValue)}
          </span>
        )}
        <span style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
          {pct != null && (
            <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:700, color:up?C.green:C.neg, whiteSpace:"nowrap" }}>
              {up?"+":""}{pct.toFixed(1)}%
            </span>
          )}
          <span className="hma-mc-chevron" style={{ fontSize:14, color:C.hint, transform:expanded?"rotate(180deg)":"rotate(0deg)" }}>
            ▾
          </span>
        </span>
      </button>

      {/* Mobile: expanded content */}
      {expanded && (
        <div style={{ padding:"2px 16px 14px" }}>
          {cardBody}
        </div>
      )}
    </div>
  );
}
